// @flow

// Skipping test coverage for this component since it is not possible to unit test it correctly
/* eslint-disable ta-code-conventions/has-matching-storydoc, ta-code-conventions/has-matching-test */

import * as React from 'react';
import { connect } from 'react-redux';
import { actions } from '@ta/common.state';
import track from '@ta/common.tracking';
import { RouteProvider } from './RouterContext';
import { buildUrl } from './canonicalUrl';
import canNavigate from './canNavigate';
import type { Route, NavigateOptions, BeforeNavigationArgs } from './types';

/**
 * Each React root has its own Router instance, so this has to be a module-level variable
 */
const beforeNavigationCallbacks: Set<(BeforeNavigationArgs) => boolean> = new Set();

const shouldStopNavigation = (args: BeforeNavigationArgs) =>
  [...beforeNavigationCallbacks].some(callback => callback(args));

type ExternalProps = {|
  children: ?React.Node,
|};

type Props = {|
  ...ExternalProps,
  navigate: (to: any, builtUrl: string) => void,
  route: Route,
|};

type State = {|
  previous: ?Route,
|};

// TODO: ideally common.routing doesn't know which pages want to be scrolled on being routed,
// but this is a feasible fix for now until we centralize routing/page configuration
const SCROLL_TOP_ROUTES = [
  'Trips',
  'TripsWebview',
  'Owners',
  'BrandOwners',
  'HotelsManagementCenter',
  'DestinationArticle',
  'HotelsManageListing',
  'SafetyManagementCenter',
];

/**
 * Passthrough component that uses Redux as the source of truth for routing. To
 * children of this component, however, use of Redux is an unknown implementation
 * detail.
 */
class Router extends React.Component<Props, State> {
  state = {
    previous: null,
  };

  record(to: Route, prev: Route, reset?: boolean = false) {
    track({ module: to.page, action: 'SPA_NAVIGATION' });

    // eslint-disable-next-line no-restricted-globals
    if (typeof window !== 'object' || !window.history) {
      return;
    }

    if (reset) {
      if (this.state.previous) {
        // eslint-disable-next-line no-restricted-globals
        window.history.back();
      } else if (prev) {
        // eslint-disable-next-line no-restricted-globals
        window.history.replaceState(to, '', buildUrl(to));
        this.setState({ previous: { ...prev } });
      } else {
        // eslint-disable-next-line no-restricted-globals
        window.history.replaceState(to, '', buildUrl(to));
        this.setState({ previous: { ...to } });
      }
    } else {
      // eslint-disable-next-line no-restricted-globals
      window.history.pushState(to, '', buildUrl(to));
      this.setState({ previous: { ...prev } });
    }
  }

  render() {
    const { route, navigate: navigateAction, children } = this.props;
    return (
      <RouteProvider
        value={{
          popping: false,
          previousRoute: null,
          route,
          addBeforeNavigationCallback: callback => beforeNavigationCallbacks.add(callback),
          removeBeforeNavigationCallback: callback => beforeNavigationCallbacks.delete(callback),
          navigate: (to: Route, options?: NavigateOptions) => {
            const { noHistory, reset } = options || {};
            const prev = { ...route };

            // True if a popstate event was stopped
            let historyStopped = false;

            const continueNavigation = () => {
              let goingToLeavePage;

              // force a location update if entering or leaving the SPA using a navigate() call
              if (!noHistory && !canNavigate(prev, to)) {
                // eslint-disable-next-line no-restricted-globals
                window.location = buildUrl(to);
                goingToLeavePage = true;
              }

              navigateAction(to, buildUrl(to));

              if (historyStopped) {
                // eslint-disable-next-line no-restricted-globals
                window.history.back();
              }

              // If true, this call came from a popstate event. Update the Redux store, but allow the
              // caller to choose how to manage the browser history.
              if (noHistory) {
                return;
              }

              // regardless of history, we want to reset scroll position on page change
              if (
                SCROLL_TOP_ROUTES.includes(to.page) &&
                SCROLL_TOP_ROUTES.includes(route.page) &&
                to !== route
              ) {
                // eslint-disable-next-line no-restricted-globals
                window.scrollTo(0, 0);
              }

              if (!goingToLeavePage) {
                this.record(to, prev, reset);
              }
            };

            if (!shouldStopNavigation({ to, continueNavigation })) {
              continueNavigation();
            } else if (noHistory) {
              // popstate events can't be canceled, but we can insert a new history entry so the
              // current state is correct.
              // eslint-disable-next-line no-restricted-globals
              window.history.pushState(prev, '', buildUrl(prev));
              historyStopped = true;
            }

            return true;
          },
        }}
      >
        {children}
      </RouteProvider>
    );
  }
}

export default connect<Props, ExternalProps, _, _, _, _>(
  (state, props: ExternalProps) => ({
    ...props,
    route: state.route || {},
  }),
  {
    navigate: actions.route.navigate,
  },
)(Router);
